/**
 * Titanvell world configuration — the ONE place this site's branding, rules
 * system, sourcebook, and factions are declared. The shared shell
 * (@gamecodex/wargame-ui) reads it via the active-config singleton.
 *
 * Titanvell is the debut sourcebook for the Battlestrike miniatures wargame
 * engine (see brain/api/todo/20260712200000Z-titanvell-battlestrike-sourcebook.md
 * for the authoring handoff). This config is the first "setting overlay" on
 * the wargame template — a different setting would just be a new app with its
 * own wargame.config.ts pointing at a different sourcebookId/factions/branding,
 * same rulesSystemId ('battlestrike') and same @gamecodex/wargame-ui shell.
 */
import type { WargameConfig } from '@gamecodex/wargame-config'

export const titanvellConfig: WargameConfig = {
  wargameId: 'titanvell',
  wargameName: 'Titanvell',
  domain: 'titanvell.com',

  locales: {
    default: 'en',
    supported: [{ code: 'en', label: 'English', native: 'English', dir: 'ltr' }],
  },

  content: { en: {} },

  branding: {
    images: {
      logo: '/logo.png',
      ogImage: '/og/default.svg',
      favicon: '/favicon.svg',
    },
    logoIsWordmark: true,
    fonts: {
      display: 'Oswald',
      heading: 'Oswald',
      body: 'Inter',
      stylesheetUrl:
        'https://fonts.googleapis.com/css2?family=Oswald:wght@500;600;700;800&family=Inter:wght@400;500;600&display=swap',
    },
    background: { variant: 'grid', params: { color: '#18181b' } },
    // Purple / Blue / Yellow / Red — evocative of the hero Titan art (violet
    // mace-wielder, gold-and-blue priestess). Teal and orange are retired.
    colors: {
      primary: '#a855f7',
      accent: '#3b82f6',
    },
  },

  // The api `game_system.code` for the Battlestrike rules engine.
  rulesSystemId: 'battlestrike',
  // The api `pub_book.id` for the "Titanvell" sourcebook (created 2026-07-13).
  sourcebookId: '93941c87-5ac6-4373-9fd7-eb4be6b5ff1c',

  factions: [
    {
      id: 'titan',
      label: 'Titans',
      blurb: 'AI-aligned. Cybernetics, giant weapons, and battlefield hacking.',
      color: '#3b82f6',
    },
    {
      id: 'kaiju',
      label: 'Kaiju',
      blurb: 'Radiation-aligned. Devastating melee and breath weapons.',
      color: '#ef4444',
    },
    {
      id: 'mecha',
      label: 'Mecha',
      blurb: 'Energy-aligned. Tech, missiles, and robotics.',
      color: '#eab308',
    },
  ],

  homeIntro: {
    eyebrow: 'CONSTRUCT - EVOLVE - MUTATE - DESTROY',
    title: 'Evolve. Battle. Become Legend.',
    body: 'Start as a civilian, collect power, and become a Colossus with your own figures or using ours! Titanvell pits Mecha vs Kaiju vs Titans against each other in a modular, destructible world that you build and destroy!',
    ctaLabel: 'Build Your Titan',
    heroImage: '/titan-hero-left.png',
    heroImageRight: '/titan-hero-right.png',
  },

  simulator: {
    boardSize: { width: 28, height: 24 },
    diceFaces: [4, 6, 8, 10, 12, 20, 30, 60, 100],
  },

  // Physical-merchandise store. `enabled: true` turns on the /store route +
  // nav link now (browsing/cart work today, no backend dependency); checkout
  // requires Atlas to have matching SKUs + ATLAS_API_BASE configured for this
  // site's Netlify deploy — see brain/api/todo for the filed backend spec.
  // skuCode values are placeholders pending Atlas's real catalog; prices are
  // display fallbacks, overridden by the live catalog once Atlas has SKUs.
  store: {
    enabled: true,
    categories: [
      {
        id: 'terrain-kits',
        label: '3D Terrain Kit Boxed Sets',
        products: [
          {
            skuCode: 'TVL-TERRAIN-STARTER',
            name: 'Tunnel Complex Starter Set',
            description: 'Straight, corner, and intersection tiles to build your first battlefield.',
            price: 49.99,
          },
          {
            skuCode: 'TVL-TERRAIN-JUNCTION',
            name: 'Junction & Dead-End Expansion',
            description: 'Adds 3-way junctions and dead-ends for bigger, more tactical layouts.',
            price: 24.99,
          },
          {
            skuCode: 'TVL-TERRAIN-BUNDLE',
            name: 'Full Battlefield Bundle',
            description: 'Every terrain tile in one box — everything you need for a 28"x24" board.',
            price: 89.99,
          },
        ],
      },
      {
        id: 'titan-kits',
        label: 'Titan Kits',
        products: [
          { skuCode: 'TVL-KIT-TITANS', name: 'Titans Starter Kit', description: 'Base figure, Kit Card, and dice for the Titans faction.', price: 39.99 },
          { skuCode: 'TVL-KIT-KAIJU', name: 'Kaiju Starter Kit', description: 'Base figure, Kit Card, and dice for the Kaiju faction.', price: 39.99 },
          { skuCode: 'TVL-KIT-MECHA', name: 'Mecha Starter Kit', description: 'Base figure, Kit Card, and dice for the Mecha faction.', price: 39.99 },
        ],
      },
      {
        id: 'dice-sets',
        label: 'Dice Sets',
        products: [
          { skuCode: 'TVL-DICE-AWAKENED', name: 'Awakened Dice Set', description: 'd10 / d12 / d20 — everything an Awakened Colossus rolls.', price: 9.99 },
          { skuCode: 'TVL-DICE-MEGA', name: 'Mega Dice Set', description: 'd30 / d60 / d100 — the big dice for full-size Colossi.', price: 12.99 },
          { skuCode: 'TVL-DICE-FULL', name: 'Full Battlestrike Dice Set', description: 'The complete d4-through-d100 pool used across every archetype.', price: 19.99 },
        ],
      },
      {
        id: 'vehicle-kits',
        label: 'Vehicle Kits',
        products: [
          { skuCode: 'TVL-VEHICLE-DRIVABLE', name: 'Drivable Vehicle Kit', description: 'Power Core + Wheels — mount up for +4" movement.', price: 14.99 },
          { skuCode: 'TVL-VEHICLE-TURRET', name: 'Turret Kit', description: 'Power Core + Weapon — a stationary heavy hitter.', price: 9.99 },
          { skuCode: 'TVL-VEHICLE-HOPCRAFT', name: 'Hop Craft Kit', description: 'Power Core + Wings — one-time instant relocation anywhere on the table.', price: 12.99 },
        ],
      },
      {
        id: 'playmats',
        label: 'Playmats',
        products: [
          { skuCode: 'TVL-PLAYMAT-STANDARD', name: 'Tunnel Complex Playmat', description: '28"x24" neutral battlefield playmat.', price: 24.99 },
          { skuCode: 'TVL-PLAYMAT-TITANS', name: 'Titans Playmat', description: 'Titans-themed art variant.', price: 29.99 },
          { skuCode: 'TVL-PLAYMAT-KAIJU', name: 'Kaiju Playmat', description: 'Kaiju-themed art variant.', price: 29.99 },
          { skuCode: 'TVL-PLAYMAT-MECHA', name: 'Mecha Playmat', description: 'Mecha-themed art variant.', price: 29.99 },
        ],
      },
      {
        id: 'miniature-kits',
        label: 'Miniature Evolution Kits',
        products: [
          { skuCode: 'TVL-MINI-WARLORD', name: 'Warlord Evolution Kit', description: 'Civilian, Awakened, and Mega miniatures for the Titans’ Warlord archetype.', price: 19.99 },
          { skuCode: 'TVL-MINI-GHOST', name: 'Ghost Evolution Kit', description: 'Civilian, Awakened, and Mega miniatures for the Titans’ Ghost archetype.', price: 19.99 },
          { skuCode: 'TVL-MINI-LEGION', name: 'Legion Evolution Kit', description: 'Civilian, Awakened, and Mega miniatures for the Titans’ Legion archetype.', price: 19.99 },
          { skuCode: 'TVL-MINI-BRUTE', name: 'Brute Evolution Kit', description: 'Civilian, Awakened, and Mega miniatures for the Kaiju’s Brute archetype.', price: 19.99 },
          { skuCode: 'TVL-MINI-RAPTOR', name: 'Raptor Evolution Kit', description: 'Civilian, Awakened, and Mega miniatures for the Kaiju’s Raptor archetype.', price: 19.99 },
          { skuCode: 'TVL-MINI-SWARM', name: 'Swarm Evolution Kit', description: 'Civilian, Awakened, and Mega miniatures for the Kaiju’s Swarm archetype.', price: 19.99 },
          { skuCode: 'TVL-MINI-SIEGE', name: 'Siege Evolution Kit', description: 'Civilian, Awakened, and Mega miniatures for the Mecha’s Siege archetype.', price: 19.99 },
          { skuCode: 'TVL-MINI-STRIDER', name: 'Strider Evolution Kit', description: 'Civilian, Awakened, and Mega miniatures for the Mecha’s Strider archetype.', price: 19.99 },
          { skuCode: 'TVL-MINI-BATTERY', name: 'Battery Evolution Kit', description: 'Civilian, Awakened, and Mega miniatures for the Mecha’s Battery archetype.', price: 19.99 },
        ],
      },
    ],
  },

  seo: {
    defaultDescription:
      'Titanvell is a dead-simple, fast-playing skirmish wargame for 2–4 players or solo. Evolve Mecha, Kaiju, and Titans from tiny to towering using real action figures.',
    tagline: 'Evolve. Battle. Become Legend.',
    defaultOgImage: '/og/default.svg',
  },
}
